import React, { useState } from 'react';
import {
  Accordion as MUIAccordion,
  AccordionProps as MUIAccordionProps,
  AccordionSummary,
  AccordionDetails,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  Stack,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

interface AccordionProps extends MUIAccordionProps {
  title: string;
  icon?: React.ReactNode;
  subTitle?: string;
}

const BAR_WIDTH = 4;
const ACTIVE = '#00D2DC';
const INACTIVE = '#999EB0';


export default function Accordion({
  children,
  title,
  icon,
  subTitle,
  ...rest
}: AccordionProps) {
  const [expanded, setExpanded] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const sidePad = isMobile ? 12 : 16;

  const leftPad = sidePad + BAR_WIDTH;

  const summaryYPadding = expanded
    ? (isMobile ? 12 : 16)
    : (isMobile ? 2 : 4);

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Box
        sx={{
          width: BAR_WIDTH,
          borderRadius: '2px',
          bgcolor: expanded ? ACTIVE : INACTIVE,
          flexShrink: 0,
        }}
      />

      <MUIAccordion
        {...rest}
        expanded={expanded}
        onChange={(_, open) => setExpanded(open)}
        disableGutters
        sx={{
          background: '#FDFDFD',
          borderRadius: '8px',
          boxShadow: 'none',
          width: '100%',
          '&:before': { display: 'none' },
          '& .MuiCollapse-root, & .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
            width: '100%',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<FontAwesomeIcon icon={faChevronRight as any} />}
          sx={{
            padding: `${summaryYPadding}px ${sidePad}px`,
            paddingLeft: `${leftPad}px`,
            transition: 'padding 0.25s ease',
            minHeight: 'unset !important',

            '& .MuiAccordionSummary-content': { margin: 0, width: '100%' },

            '& .MuiAccordionSummary-expandIconWrapper': {
              position: 'absolute',
              right: sidePad,
              transition: 'transform 0.3s ease',
              '&.Mui-expanded': { transform: 'rotate(-90deg)' },
              '& svg': {
                width: isMobile ? 12 : 14,
                height: isMobile ? 12 : 14,
                color: '#999EB0',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {icon && (
              <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                {icon}
              </Box>
            )}

            <Stack>
              <Typography
                component="div"
                sx={{
                  color: '#55596B',
                  fontSize: isMobile ? 14 : 16,
                  fontFamily: 'Montserrat',
                  fontWeight: 800,
                  lineHeight: isMobile ? '20px' : '24px',
                  letterSpacing: '0.5px',
                  paddingRight: isMobile ? '32px' : '40px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography>

              {subTitle && (
                <Typography
                  component="div"
                  sx={{
                    color: '#999EB0',
                    fontSize: isMobile ? 12 : 14,
                    fontFamily: 'Montserrat',
                    fontWeight: 400,
                    lineHeight: isMobile ? '16px' : '20px',
                    paddingRight: isMobile ? '32px' : '40px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {subTitle}
                </Typography>
              )}
            </Stack>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            padding: isMobile ? '0 12px 12px' : '0 16px 16px',
            paddingLeft: `${leftPad}px`,
            width: '100%',
            '& > *': { width: '100%' },
          }}
        >
          {children}
        </AccordionDetails>
      </MUIAccordion>
    </Box>
  );
}